import z from 'zod';

export const defaultExperimentSet = 'control';

export const experimentCookieKey = 'ci_exp_uid';

// Note: As we find out about upcoming experiments, add them here.
// These names should match exactly with the name of the flag in Optimizely.
// Adhoc ones are for quick testing and experimentation without needing to define them in advance.
export const KnownExperiments = z.enum([
  'adhoc1',
  'adhoc2',
  'adhoc3',
  'adhoc4',
  'adhoc5',
  'checkoutChildImageSize',
  'checkoutProgressiveForm',
  'checkoutRecommendedBadges',
  'checkoutReviewStickySubmit',
  'checkoutSideSwap',
  'homeChildReel',
  'homeCurrentSponsorCards',
  'homeHero',
  'homeSacFilterHero',
  'needBadges',
  'sacAdvancedFilters',
  'sacHero',
  'sacSortLongestWaiting',
  'sacTabbedInfo',
  'sublinks',
]);

const segmentMarker = '_';

export const filterExperiments = (experiments: string[]): string[] => {
  const safe = experiments
    .map((e) => (KnownExperiments.safeParse(e).success ? e : undefined))
    .filter(Boolean) as string[];
  if (!safe.length) {
    return [defaultExperimentSet];
  }
  const unique = [...new Set(safe)];
  return unique;
};

export const encodeExperiments = (experiments: string[]): string => {
  return filterExperiments(experiments).join(segmentMarker);
};

export const decodeExperiments = (encodedExperiments: string): string[] => {
  return filterExperiments(encodedExperiments.split(segmentMarker));
};

export const experimentsToClassnames = (experiments: string[]): string => {
  return filterExperiments(experiments)
    .map((e) => `experiment-${e}`)
    .join(' ');
};

export const OptimizelyUserAttributes = z.object({
  pathname: z.string().optional().nullable(),
  hostname: z.string().optional().nullable(),
  utmSource: z.string().optional().nullable(),
  utmMedium: z.string().optional().nullable(),
  utmCampaign: z.string().optional().nullable(),
  utmContent: z.string().optional().nullable(),
  rscReferrer: z.string().optional().nullable(),
  rscSourceCode: z.string().optional().nullable(),
});
export type OptimizelyUserAttributes = z.infer<typeof OptimizelyUserAttributes>;

export const OptimizelyResults = z
  .object({
    campaignId: z.string().optional().nullable(),
    enabled: z.boolean().optional(),
    experimentId: z.string().optional().nullable(),
    flagKey: z.string().optional().nullable(),
    reasons: z.string().array().optional(),
    ruleKey: z.string().optional().nullable(),
    variationId: z.string().optional().nullable(),
    variationKey: z.string().optional().nullable(),
  })
  .array();
export type OptimizelyResults = z.infer<typeof OptimizelyResults>;

export const OptimizelyCookieSchema = z.object({
  uid: z.string().optional(),
});

/**
 * Serialize a uid into the canonical experiment-cookie value.
 * Keep this the single source of truth for the stored representation so
 * the middleware and route handlers agree on what "already normalized"
 * looks like.
 */
export const serializeExperimentCookie = (uid: string): string =>
  JSON.stringify({ uid });

/**
 * Extract the uid from a raw experiment-cookie value.
 *
 * Tolerates two forms:
 *   1. The canonical JSON form `{"uid":"..."}` written by current code.
 *   2. A legacy bare-string uid (e.g. `abc123`) written by an older
 *      version. `JSON.parse` throws on these, so we fall back to treating
 *      the raw value as the uid rather than discarding it.
 *
 * Returns `undefined` when the value is empty or has no usable uid.
 */
export const readUidFromCookieValue = (
  rawValue: string | undefined,
): string | undefined => {
  if (!rawValue) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    // Legacy bare-string uid — reuse it as-is.
    return rawValue;
  }

  const cookie = OptimizelyCookieSchema.safeParse(parsed);
  return cookie.success && cookie.data.uid ? cookie.data.uid : undefined;
};
